import { ArrowLeftOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Button, Col, Flex, Form, Input, Radio, Row, Select, Space, Tooltip } from 'antd';
import { MessageEventTypeEnum } from 'api/MessagesApi';
import { useMessageService } from 'components/Message/useMessageService';
import { BubbleHintComponent, BubbleHintOption } from 'components/UiKit/BubbleHintComponent/BubbleHintComponent';
import { useEffect, useState } from 'react';
import { checkIfDateValid, generateRange, getMonthNames, logging } from 'utils/utils';
import './StepEvent.css';

type AllProps = {
  initEventType?: MessageEventTypeEnum;
  onNext: (shouldExit?: boolean) => void;
  onBack: () => void;
};

function StepEvent({ initEventType, onNext, onBack }: AllProps) {
  const [eventType, setEventType] = useState<MessageEventTypeEnum>(initEventType || MessageEventTypeEnum.Final);
  const [isDisplayYear, setIsDisplayYear] = useState(true);
  const [dateError, setDateError] = useState('');
  const [form] = Form.useForm();

  const { message, updateIsLoading, updateMessageEvent } = useMessageService();

  useEffect(() => {
    if (message) {
      form.setFieldsValue({
        eventType: message.eventType || MessageEventTypeEnum.Final,
        eventHint: message.eventHint,
        deliveryYear: message.deliveryYear,
        deliveryMonth: message.deliveryMonth,
        deliveryDay: message.deliveryDay
      });
      setEventType((message.eventType || MessageEventTypeEnum.Final) as MessageEventTypeEnum);
      setIsDisplayYear(!((message.deliveryMonth || message.deliveryDay) && message?.deliveryYear === null));
    }
  }, [form, message]);

  const eventTypeOptions: BubbleHintOption[] = [
    {
      value: MessageEventTypeEnum.Final,
      title: 'At Passing',
      description:
        'This message is designed to be delivered shortly after your passing. Since this date is unknown all you’ll need to provide is the recipient(s) information and we’ll take care of the rest.'
    },
    {
      value: MessageEventTypeEnum.Birthday,
      title: 'Birthday',
      description:
        'Commemorate a special birthday! By default Birthday messages are delivered at the first occurrence following your passing unless we are provided a specific year the message is to be delivered.'
    },
    {
      value: MessageEventTypeEnum.Holiday,
      title: 'Holiday',
      description:
        'Be there with your loved ones on the days that matter! By default Holiday messages are delivered at the first occurrence following your passing, unless we are provided a specific year the message is to be delivered.'
    },
    {
      value: MessageEventTypeEnum.Anniversary,
      title: 'Anniversary',
      description:
        'Celebrate your love one more time! By default Anniversary messages are delivered at the first occurrence following your passing, unless we are provided a specific year the message is to be delivered.'
    },
    {
      value: MessageEventTypeEnum.Other,
      title: 'Other/Custom',
      description: "Leave a custom message for any occasion. Just insert the date and we'll take care of the rest."
    },
    {
      value: MessageEventTypeEnum.Future,
      title: 'Future Event',
      label: 'Future Event (Set Date Later)',
      description:
        'This message is intended for a meaningful future moment, but the exact date hasn’t been set yet. You or an administrator can update the delivery date at any time to ensure it’s sent when the moment arrives. To avoid confusion later, be sure to give your message a clear name so it’s easy to identify when the date is added.'
    }
  ];

  const isEventWithDate = () => {
    return eventType !== MessageEventTypeEnum.Final && eventType !== MessageEventTypeEnum.Future;
  };

  const isPastDeliveryDate = (day: number, month: number, year: number | null) => {
    if (year === null) {
      // Yearless delivery dates are recurring; they will be scheduled for the next occurrence.
      return false;
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const selectedDate = new Date(year, month - 1, day);

    return selectedDate < today;
  };
  const handleSubmit = async (shouldExit = false) => {
    try {
      setDateError('');

      await form.validateFields();
      const fields = form.getFieldsValue();

      if (isEventWithDate()) {
        if (!checkIfDateValid(fields.deliveryDay, fields.deliveryMonth, isDisplayYear ? fields.deliveryYear : null)) {
          setDateError('This date is invalid');
          return;
        }

        if (isPastDeliveryDate(fields.deliveryDay, fields.deliveryMonth, isDisplayYear ? fields.deliveryYear : null)) {
          setDateError('Delivery date cannot be in the past');
          return;
        }
      }

      await updateMessageEvent({
        eventType,
        eventHint: fields.eventHint,
        deliveryYear: fields.deliveryYear,
        deliveryMonth: fields.deliveryMonth,
        deliveryDay: fields.deliveryDay
      });

      onNext(shouldExit);
    } catch (e) {
      logging(e);
    }
  };

  return (
    <div className="step-event create-message-component-full-height">
      <Form
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
        requiredMark="optional"
        form={form}
        initialValues={{ eventType }}
      >
        <div className="create-message-component-title">
          What event will initiate the process to deliver your messages?
        </div>
        <p>
          <strong>Select the option that best applies.</strong>
        </p>
        <BubbleHintComponent
          options={eventTypeOptions}
          selectedValue={eventType}
          onChange={(value) => setEventType(value as MessageEventTypeEnum)}
        />

        {eventType === MessageEventTypeEnum.Future && (
          <div className="select-delivery-date">
            <Form.Item
              label={<strong>Event details</strong>}
              name="eventHint"
              rules={[{ required: true, message: 'Please enter event details' }]}
            >
              <Input placeholder="Specify the event that will initiate the process to deliver your messages" />
            </Form.Item>
          </div>
        )}

        {isEventWithDate() && (
          <div className="select-delivery-date">
            <p>
              <strong>Do you know the year you would like this message delivered?</strong>{' '}
              <Tooltip title="Messages will only be delivered if your administrator has marked you as deceased">
                <InfoCircleOutlined className="cursor-pointer" />
              </Tooltip>
            </p>

            <Radio.Group
              onChange={(e) => setIsDisplayYear(e.target.value)}
              value={isDisplayYear}
              className="is-display-year"
            >
              <Space direction="horizontal">
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Space>
            </Radio.Group>

            <Row gutter={10} className="delivery-date-inputs">
              {isDisplayYear && (
                <Col span={8}>
                  <Form.Item name="deliveryYear" messageVariables={{ label: 'Year' }} rules={[{ required: true }]}>
                    <Select
                      placeholder="Year"
                      options={generateRange(new Date().getFullYear(), new Date().getFullYear() + 50).map((year) => ({
                        label: year,
                        value: year
                      }))}
                    ></Select>
                  </Form.Item>
                </Col>
              )}
              <Col span={8}>
                <Form.Item name="deliveryMonth" messageVariables={{ label: 'Month' }} rules={[{ required: true }]}>
                  <Select
                    placeholder="Month"
                    options={getMonthNames().map((month, index) => ({
                      label: month,
                      value: index + 1
                    }))}
                  ></Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="deliveryDay" messageVariables={{ label: 'Day' }} rules={[{ required: true }]}>
                  <Select
                    placeholder="Day"
                    options={generateRange(1, 31).map((day) => ({
                      label: day,
                      value: day
                    }))}
                  ></Select>
                </Form.Item>
              </Col>
            </Row>
            {dateError && <div className="date-error">{dateError}</div>}
          </div>
        )}
      </Form>

      <Row className="create-message-component-footer">
        <Col span={12}>
          <Button onClick={onBack} type="link">
            <ArrowLeftOutlined /> Back
          </Button>
        </Col>
        <Col span={12} className="text-right">
          <Flex gap={20} justify="flex-end">
            <Button onClick={() => handleSubmit(true)}>Save & Exit</Button>
            <Button onClick={() => handleSubmit()} type="primary" loading={updateIsLoading}>
              Next
            </Button>
          </Flex>
        </Col>
      </Row>
    </div>
  );
}

export default StepEvent;
